/**
 * Google Analytics 4 Utility
 * Tracks page views, conversions, and custom events
 */

const GA_MEASUREMENT_ID = 'G-39QVWEL7YE';

// Get stored UTM parameters
export const getUtmParams = () => {
  const params = {};
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  utmKeys.forEach(key => {
    const value = sessionStorage.getItem(key);
    if (value) params[key] = value;
  });
  return params;
};

// Check if gtag is available
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track page view
 * @param {string} path - The page path
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
    ...getUtmParams()
  });
};

/**
 * Track custom event
 * @param {string} eventName - Name of the event
 * @param {object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('event', eventName, {
    ...params,
    ...getUtmParams()
  });
};

/**
 * Track conversion events
 */
export const trackConversion = {
  // User signed up
  signup: (method = 'email') => {
    trackEvent('sign_up', {
      method,
      event_category: 'engagement',
      event_label: 'New user registration'
    });
  },

  // User logged in
  login: (method = 'email') => {
    trackEvent('login', {
      method,
      event_category: 'engagement'
    });
  },

  // User completed onboarding
  onboardingComplete: () => {
    trackEvent('tutorial_complete', {
      event_category: 'engagement',
      event_label: 'Onboarding completed'
    });
  },

  // User started checkout
  beginCheckout: (plan, price) => {
    trackEvent('begin_checkout', {
      currency: 'USD',
      value: price,
      items: [{
        item_id: plan,
        item_name: plan === 'pro_monthly' ? 'Pro Monthly' : 'Pro Yearly',
        price: price
      }]
    });
  },

  // User completed purchase
  purchase: (plan, price, transactionId) => {
    trackEvent('purchase', {
      transaction_id: transactionId,
      currency: 'USD',
      value: price,
      items: [{
        item_id: plan,
        item_name: plan === 'pro_monthly' ? 'Pro Monthly' : 'Pro Yearly',
        price: price
      }]
    });
  },

  // User sent first message
  firstMessage: () => {
    trackEvent('first_message', {
      event_category: 'engagement',
      event_label: 'User sent first chat message'
    });
  },

  // User created memory
  memoryCreated: () => {
    trackEvent('memory_created', {
      event_category: 'engagement'
    });
  },

  // User uploaded document
  documentUploaded: () => {
    trackEvent('document_uploaded', {
      event_category: 'engagement'
    });
  },

  // User shared conversation
  conversationShared: () => {
    trackEvent('share', {
      content_type: 'conversation',
      event_category: 'engagement'
    });
  },

  // Landing page CTA clicked
  ctaClicked: (ctaName, location) => {
    trackEvent('cta_click', {
      event_category: 'engagement',
      event_label: ctaName,
      cta_location: location
    });
  },

  // Pricing page viewed
  pricingViewed: () => {
    trackEvent('view_item_list', {
      item_list_name: 'Pricing Plans',
      event_category: 'ecommerce'
    });
  },

  // Discount code applied
  discountApplied: (code, discountAmount) => {
    trackEvent('select_promotion', {
      promotion_name: code,
      discount: discountAmount
    });
  }
};

/**
 * Set user ID for cross-device tracking
 * @param {string} userId - The user's unique ID
 */
export const setUserId = (userId) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: userId
  });
};

/**
 * Set user properties
 * @param {object} properties - User properties to set
 */
export const setUserProperties = (properties) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('set', 'user_properties', properties);
};

export default {
  trackPageView,
  trackEvent,
  trackConversion,
  setUserId,
  setUserProperties,
  getUtmParams
};
